<template>
    <a class="col-md-6 mb-0" style="display: block;">
        <div class="card custom-card">
            <div class="row mx-3">
                <div class="col-6 d-flex justify-content-start">
                    <small v-if="!loading && data" class="font-weight-bolder">Próxima Visita</small>
                </div>
                <div class="col-6 d-flex justify-content-end">
                    <span v-if="!loading">{{ data ? formatDate(data.schedulingDate) : '' }}</span>
                </div>
            </div>
            <div v-if="loading" class="d-flex justify-content-center align-items-center col-12" style="height: 100px;">
                <p><i class="fas fa-spinner fa-pulse"></i> Buscando agendamentos</p>
            </div>
            <div v-if="!loading && !data" class="waiting-messages col-12">
                <div class="d-flex justify-content-center segment-primary-color row waiting-message-text">
                    Você não possui visitas agendadas
                </div>
                <div>
                    <div class="d-flex justify-content-center row waiting-message-subtext">
                        <span class="mb-3">{{ message }}</span>
                        <div v-if="authorization" class="button-row d-flex justify-content-center">
                            <argon-button type="button" class="js-btn-next custom-btn btn bg-segment-primary-color align-items-center btn-sm" @click="toggleComponent('Schedule')">
                                Agendar
                            </argon-button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!loading && data" class="row mx-3">
                <div class="col-6 segment-primary-color d-flex justify-content-start font-weight-bolder"
                    style="font-size: 15px;">
                    <span>{{ "Em " + leftDays(data.schedulingDate) + " Dia(s)" }}</span>
                </div>
                <div class="col-6 d-flex justify-content-end">
                    <span>{{ formatHour(data.schedulingDate) }}</span>
                </div>
            </div>
            <div v-if="!loading && data" class="row mx-3 mt-auto d-flex justify-content-center">
                <div class="col-6 col-6 col-sm-6 col-md-12 col-lg-6 d-flex">
                    <button class="btn custom-btn custom-btn-cancel btn-sm" @click="cancel(data.schedulingId)">
                        Cancelar <i v-if="loadingCancel" class="fa fa-spinner fa-pulse fa-fw"></i>
                    </button>
                </div>
            </div>
        </div>
    </a>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from "axios";
import { useStore } from "vuex";
import moment from 'moment';
import { showToast } from "@/assets/js/toast.js";
import ArgonButton from "@/components/ArgonButton.vue";

const store = useStore();
const loading = ref(true);
const loadingAuthorizatoin = ref(true);
const loadingCancel = ref(false);
const error = ref(null);
const data = ref(null);
const authorization = ref(false);
const releaseDate = ref(false);
const message = ref();
const contractData = computed(() => store.getters.getCurrentContract);

onMounted(() => {
    getClientSchedules();
    getAuthorization();
});

const getClientSchedules = async () => {
    try {
        loading.value = true;
        const response = await axios.get(`/clients/scheduling/scheduling-salesforce/${contractData.value.contrato_id}`);
        data.value = getNextSchedule(response.data.message);
    } catch (err) {
        console.error(err.response)
        showToast(`Nao foi possível buscar os próximos agendamentos`, 'error')
        error.value = err.message;
    } finally {
        loading.value = false;
    }
}

const toggleComponent = (component) => {
    store.commit("updateComponent", component)
}

const getAuthorization = async () => {
    try {
        loadingAuthorizatoin.value = true;
        const response = await axios.get(`/clients/scheduling/salesforce/authorization/${contractData.value.contrato_id}/${contractData.value.id_empreendimento}`);
        authorization.value = response.data.authorization
        releaseDate.value = response.data.releaseDate

        if (authorization.value) {
            message.value = `A ${response.data.scheduleType} do seu empreendimento está liberada`
        } else if(!releaseDate.value) {
            message.value = `A Visita do seu empreendimento não está liberada`
        } else if (releaseDate.value) {
            message.value = `A Visita da sua unidade está liberada a partir de ${releaseDate.value}`
        }
    } catch (err) {
        error.value = err.message;
    } finally {
        loadingAuthorizatoin.value = false;
    }
}

const getNextSchedule = (schedulings) => {
    return schedulings
        .filter(({ status, schedulingDate }) => status === "Confirmado" && new Date(schedulingDate) > new Date())
        .sort((a, b) => new Date(a.schedulingDate) - new Date(b.schedulingDate))[0] || null;
}

const cancel = async (scheduleId) => {
    loadingCancel.value = true;
    try {
        await axios.put(`/clients/scheduling/salesforce/${scheduleId}`);
        getClientSchedules();
        showToast('Agendamento cancelado com sucesso', 'success');
    } catch (err) {
        error.value = err.message;
    } finally {
        loadingCancel.value = false;
    }
}

const formatDate = (date) => {
    return moment(date).format('DD/MM/YYYY');
}

const formatHour = (date) => {
    return moment(date).format('HH:mm');
}

const leftDays = (dateTime) => {
    const today = moment();
    const date = moment(dateTime, "YYYY-MM-DD HH:mm:ss");
    const diffDays = date.diff(today, 'days');
    return diffDays;
}

const watcherFunction = () => {
    return (newValue, oldValue) => {
        if (newValue && oldValue && typeof newValue === 'object' && typeof oldValue === 'object' && newValue.contrato_unidade !== oldValue.contrato_unidade) {
            getClientSchedules()
            getAuthorization()
        }
    };
};
watch(contractData, watcherFunction());
</script>

<style scoped>
.custom-card {
    background-color: #ffff;
    width: 100%;
    height: 190px;
    padding: 16px;
    /* Add padding */
    overflow: hidden;
    /* Prevent overflow */
    position: relative;
    /* Ensure positioning context */
}

.waiting-messages {
    text-align: center;
    /* Center the text */
}

.waiting-message-text {
    font-size: 0.9rem;
    line-height: 1.4rem;
    padding-bottom: 14px;
    font-weight: 600;
    white-space: normal;
}

.waiting-message-subtext {
    padding: 0 20px;
    line-height: 1.1rem;
    font-size: 12px;
    white-space: normal;
    /* Allow text to wrap */
}

.custom-btn-cancel {
    background-color: #F7B7C3;
    color: #f74d4d !important;
}
</style>
